import React,{ useEffect,useMemo,useState } from "react";
import { ToastContainer,toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { DndContext,closestCenter } from "@dnd-kit/core";
import {
    SortableContext,
    useSortable,
    arrayMove,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import {
    useGetHomeRowsQuery,
    useUpdateHomeRowMutation,
    useSaveHomeRowsOrderMutation,
} from "../../services/modules/homeRows";

const HomeRowSeed = () => {
    const { data,isLoading,refetch,isFetching } = useGetHomeRowsQuery();
    const [updateHomeRow] = useUpdateHomeRowMutation();
    const [saveHomeRowsOrder] = useSaveHomeRowsOrderMutation();

    const rows = data?.rows || data?.data?.rows || [];
    const [orderedRows,setOrderedRows] = useState([]);

    // sincronizza l'array ordinato quando arrivano dati nuovi
    useEffect(() => {
        setOrderedRows((prev) => {
            const sorted = [...rows].sort((a,b) => (a.displayOrder || 0) - (b.displayOrder || 0));
            if (JSON.stringify(prev.map((x) => x.id)) !== JSON.stringify(sorted.map((x) => x.id))) {
                return sorted;
            }
            return prev;
        });
    },[rows]);

    // ---- DRAG & DROP ----
    const handleDragEnd = (event) => {
        const { active,over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = orderedRows.findIndex((r) => r.id.toString() === active.id);
        const newIndex = orderedRows.findIndex((r) => r.id.toString() === over.id);
        setOrderedRows(arrayMove(orderedRows,oldIndex,newIndex));
    };

    // Salva ordine backend (array di id in ordine)
    const saveOrder = async () => {
        try {
            const ids = orderedRows.map((r) => r.id);
            await saveHomeRowsOrder({ ids }).unwrap();
            toast.success("Ordine salvato!");
            await refetch();
        } catch (err) {
            console.error("Errore salvataggio ordine",err);
            toast.error("Errore nel salvataggio dell'ordine");
        }
    };

    // Toggle Homepage (isActive) con update ottimistico come in CategoryList
    const toggleHomepage = async (row) => {
        const next = !row.isActive;

        // update ottimistico nello stato locale
        setOrderedRows((prev) =>
            prev.map((r) => (r.id === row.id ? { ...r,isActive: next } : r))
        );

        try {
            await updateHomeRow({ id: row.id,isActive: next }).unwrap();
            toast.success("Stato homepage aggiornato");
            await refetch();
        } catch (err) {
            // rollback in caso di errore
            setOrderedRows((prev) =>
                prev.map((r) => (r.id === row.id ? { ...r,isActive: row.isActive } : r))
            );
            console.error("Errore aggiornamento homepage:",err);
            toast.error("Errore aggiornamento stato homepage");
        }
    };

    const typeBadge = (type) => {
        const base = "px-2 py-0.5 rounded text-xs font-medium";
        switch (type) {
            case "TOP5":
                return <span className={`${base} bg-purple-100 text-purple-700`}>TOP5</span>;
            case "RECOMMENDED":
                return <span className={`${base} bg-blue-100 text-blue-700`}>RECOMMENDED</span>;
            case "CATEGORY":
                return <span className={`${base} bg-emerald-100 text-emerald-700`}>CATEGORY</span>;
            case "PLAYLISTCATEGORY":
                return <span className={`${base} bg-amber-100 text-amber-700`}>PLAYLIST CATEGORY</span>;
            default:
                return <span className={`${base} bg-gray-100 text-gray-700`}>{type}</span>;
        }
    };

    return (
        <div className="p-4">
            <ToastContainer position="top-right" autoClose={2500} style={{ zIndex: 9999 }} />
            <h2 className="text-xl font-bold mb-6">Ordine homepage</h2>

            {(isLoading || isFetching) ? (
                <p>Loading righe...</p>
            ) : orderedRows.length > 0 ? (
                <>
                    <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <table className="min-w-full bg-white border text-sm">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="border px-2 py-1 w-[90px]">Ordina</th>
                                    <th className="border px-2 py-1">ID</th>
                                    <th className="border px-2 py-1">Tipo</th>
                                    <th className="border px-2 py-1">Sezione</th>
                                    <th className="border px-2 py-1 w-[160px]">Homepage</th>
                                </tr>
                            </thead>

                            <SortableContext
                                items={orderedRows.map((r) => r.id.toString())}
                                strategy={verticalListSortingStrategy}
                            >
                                <tbody>
                                    {orderedRows.map((row) => (
                                        <SortableRow
                                            key={row.id}
                                            row={row}
                                            typeBadge={typeBadge}
                                            toggleHomepage={toggleHomepage}
                                        />
                                    ))}
                                </tbody>
                            </SortableContext>
                        </table>
                    </DndContext>

                    <div className="flex justify-end mt-3">
                        <button onClick={saveOrder} className="bg-red-600 text-white px-4 py-2 rounded">
                            Salva Ordine
                        </button>
                    </div>
                </>
            ) : (
                <p className="text-gray-500">Nessuna riga trovata.</p>
            )}
        </div>
    );
};

export default HomeRowSeed;

// --- SORTABLE ROW ---
const SortableRow = ({ row,typeBadge,toggleHomepage }) => {
    const { attributes,listeners,setNodeRef,transform,transition } = useSortable({
        id: row.id.toString(),
    });
    const style = { transform: CSS.Transform.toString(transform),transition };

    const label = useMemo(() => {
        switch (row.type) {
            case "CATEGORY":
                return row?.category?.name || "-";
            case "PLAYLISTCATEGORY":
                return row?.playlistCategory?.name || "-";
            case "TOP5":
                return "Top 5";
            case "RECOMMENDED":
                return "Consigliati";
            case "CONTINUA_GUARDARE":
                return "Continua a guardare";
            case "ULTIMI_VIDEO":
                return "Ultimi video";
            default:
                return row.type || "-";
        }
    },[row]);

    return (
        <tr ref={setNodeRef} style={style} className="cursor-grab hover:bg-gray-50">
            <td className="border px-2 py-1" {...attributes} {...listeners}>
                <svg
                    width="22"
                    height="22"
                    className="inline"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-label="Spostamento verticale"
                >
                    {/* linea verticale */}
                    <path d="M12 4v16" stroke="gray" strokeWidth="2" strokeLinecap="round" />
                    {/* freccia su */}
                    <path d="M8 8l4-4 4 4" stroke="gray" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    {/* freccia giù */}
                    <path d="M8 16l4 4 4-4" stroke="gray" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </td>
            <td className="border px-2 py-1">{row.id}</td>
            <td className="border px-2 py-1">{typeBadge(row.type)}</td>
            <td className="border px-2 py-1">{label}</td>
            <td className="border px-2 py-1 text-left w-[160px]">
                <div className="flex items-center gap-2">
                    <input
                        type="checkbox"
                        checked={row.isActive === true}
                        onChange={() => toggleHomepage(row)}
                        className="h-4 w-4"
                        title="Mostra in homepage"
                    />
                    <label>Homepage</label>
                </div>
            </td>
        </tr>
    );
};
